import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import {
    CANCEL,
    MY_PRODUCTS_ADD,
    MY_PRODUCTS_EDIT,
    MY_PRODUCTS_EDIT_CANCEL,
    MY_PRODUCTS_SELECT_ENTER_SOME_CHARS,
    USER_STROLLERS_ADD_NEXT_STROLLER,
    USER_STROLLERS_ADD_STROLLER,
    USER_STROLLERS_SELECT_NO_RESULTS,
} from 'mk/autogenerated/translations/MyProducts.7cbe8cc11fe8f9aa612ad65fb4aaffc5'
import { MyProductAdd, MyProductPlaceholder, MyStrollerProduct } from 'mk2/apps/blogs/components/MyProduct';
import { MenuItem } from 'mk2/components/menu/MenuItem';
import { Btn, BtnType } from 'mk2/components/Btn';
import { Panel } from 'mk2/components/Panel';
import { Menu } from 'mk2/containers/Menu/Menu';
import { menuToggle } from 'mk2/containers/Menu/Menu.actions';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { ProfileProduct, ProfileProductType } from 'mk2/schemas';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Select, { createFilter } from 'react-select';
import styles from './MyProducts.mscss';

export const MY_PRODUCTS_MENU_MORE = 'my-products-menu-more';

enum Mode {
    None,
    Editing,
    Adding,
}

export interface Option {
    value: string | number;
    label: string;
    isDisabled?: boolean;
}

interface OwnProps {
    className?: string;
    title: React.ReactNode;
    products: ProfileProduct[];
    options: Option[];
    canManage: boolean;
    canReview: boolean;
    isAdding?: boolean;
    pathname?: string;

    onRemove(product: ProfileProduct);
    onAdd(id: Option['value']);
    onSearchOptions(term: string);
}

interface DispatchProps {
    onToggleMenuMore();
}

type Props = OwnProps & DispatchProps;

function noOptionMessage({ inputValue }) {
    return USER_STROLLERS_SELECT_NO_RESULTS;
}

const filtering = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'any',
});

function filterOption(candidate, input) {
    return (input && candidate.label === '...') || filtering(candidate, input);
}

const MyProducts: React.FunctionComponent<Props> = ({
    className,
    title,
    products,
    onToggleMenuMore,
    options,
    onAdd,
    onRemove,
    onSearchOptions,
    canManage,
    canReview,
    isAdding = false,
    pathname,
}) => {
    const [mode, setMode] = useState<Mode>(Mode.None);
    const [selected, setSelected] = useState<Option['value']>(null);

    const onEditClick = useEventCallback(() => {
        setMode((v) => (v === Mode.Editing ? Mode.None : Mode.Editing));
    }, [setMode]);

    const onAddClick = useEventCallback(() => {
        setMode((v) => (v === Mode.Adding ? Mode.None : Mode.Adding));
    }, [setMode]);

    const onAddSubmit = useEventCallback(() => {
        if (!selected) {
            return;
        }

        onAdd(selected);
        setMode(Mode.None);
    }, [setMode]);

    const onRemoveCallback = useEventCallback(
        (id: string) => {
            const product = products.find((p) => `${p.type}-${p.id}` === id);
            if (product) {
                onRemove(product);
            }
        },
        [onRemove, products],
    );

    useEffect(() => {
        if (products.length === 0 && mode === Mode.Editing) {
            setMode(Mode.None);
        }
    }, [products.length, mode, setMode]);

    const onChange = useEventCallback(
        (option) => {
            if (!option) {
                onSearchOptions('');
            }
            setSelected(option?.value ?? null);
        },
        [setSelected],
    );

    return (
        <Panel className={cx(styles.MyProducts, className)}>
            <div className={styles.MyProducts__header}>
                <h3 className={styles.MyProducts__header__title}>{title}</h3>
                {canManage && products.length > 0 && (
                    <div className={styles.MyProducts__header__actions}>
                        <Menu
                            menuId={MY_PRODUCTS_MENU_MORE}
                            triggerShadow={false}
                            trigger={<Btn type={BtnType.GreyText} icon={faChevronDown} onClick={onToggleMenuMore} />}
                        >
                            <MenuItem
                                type={BtnType.Blue}
                                label={mode === Mode.Editing ? MY_PRODUCTS_EDIT_CANCEL : MY_PRODUCTS_EDIT}
                                icon={faPencil}
                                onClick={onEditClick}
                            />
                        </Menu>
                    </div>
                )}
            </div>
            <div className={styles.MyProducts__products}>
                {products.map((p) =>
                    p.type === ProfileProductType.STROLLER ? (
                        <MyStrollerProduct
                            slug={p.slug}
                            name={p.name}
                            imageUrl={p.imageUrl}
                            imageWidth={p.imageWidth}
                            imageHeight={p.imageHeight}
                            reviewId={p.reviewId}
                            reviewerScore={p.reviewerScore}
                            reviewStatus={p.reviewStatus}
                            id={`stroller-${p.id}`}
                            key={p.id}
                            className={styles.MyProducts__products__product}
                            isEditing={mode === Mode.Editing}
                            onRemove={onRemoveCallback}
                            canReview={canReview}
                            pathname={pathname}
                        />
                    ) : null,
                )}
                {canManage &&
                    mode === Mode.None &&
                    (isAdding ? (
                        <MyProductPlaceholder className={styles.MyProducts__products__product} />
                    ) : (
                        <MyProductAdd
                            label={
                                products.length === 0 ? USER_STROLLERS_ADD_STROLLER : USER_STROLLERS_ADD_NEXT_STROLLER
                            }
                            className={styles.MyProducts__products__product}
                            onClick={onAddClick}
                        />
                    ))}
            </div>
            {mode === Mode.Adding && (
                <div className={styles.MyProducts__form}>
                    <div className={styles.MyProducts__form__options}>
                        <Select
                            placeholder={MY_PRODUCTS_SELECT_ENTER_SOME_CHARS}
                            isClearable
                            isSearchable
                            onChange={onChange}
                            onInputChange={onSearchOptions}
                            options={options}
                            noOptionsMessage={noOptionMessage}
                            filterOption={filterOption}
                            className={styles.MyProducts__form__options__field}
                            menuPortalTarget={typeof document !== 'undefined' ? document.querySelector('#app') : null}
                            autoFocus
                        />
                    </div>
                    <Btn
                        className={styles.MyProducts__form__submit}
                        type={BtnType.Blue}
                        label={MY_PRODUCTS_ADD}
                        onClick={onAddSubmit}
                        disabled={!selected}
                    />
                    <Btn
                        className={styles.MyProducts__form__cancel}
                        type={BtnType.Link}
                        label={CANCEL}
                        onClick={onAddClick}
                    />
                </div>
            )}
        </Panel>
    );
};

MyProducts.displayName = 'MyProducts';

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onToggleMenuMore: () => menuToggle(MY_PRODUCTS_MENU_MORE),
};

export default connect(null, mapDispatchToProps)(MyProducts);
